import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaCircle, FaChevronDown, FaChevronUp, FaShoppingBasket, FaSearch, FaPrint, FaCheckSquare, FaFilter, FaCalendarAlt, FaSync, FaSave, FaExclamationTriangle, FaEdit, FaTimes, FaPlus, FaTrash, FaInfoCircle, FaShoppingCart, FaBan, FaCloudDownloadAlt, FaCaretDown } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { format, addDays, startOfWeek, endOfWeek, parseISO, isValid } from 'date-fns';
import { 
  fetchShoppingList, 
  saveShoppingList, 
  checkShoppingList,
  updateShoppingListView,
  removeShoppingListItem,
  deleteShoppingList as deleteShoppingListAction
} from '../../../state/mealPlannerSlice';
import mealPlannerService from '../../../services/mealPlannerService';
import LoadingSpinner from '../../../components/LoadingSpinner';
import MiniCalendar from './MiniCalendar';
import PrintTemplate from './ShoppingList/PrintTemplate';

// Grocery store configurations
const groceryStores = [
  { 
    name: 'Tesco',
    searchUrl: 'https://www.tesco.com/groceries/en-GB/search?query={query}&inputType=free+text',
    logoUrl: `${process.env.REACT_APP_CDN_URL}/images/tesco.png`
  },
  { 
    name: 'Sainsbury\'s',
    searchUrl: 'https://www.sainsburys.co.uk/gol-ui/SearchResults/{query}',
    logoUrl: `${process.env.REACT_APP_CDN_URL}/images/sainsburys.png`
  },
  { 
    name: 'Morrisons',
    searchUrl: 'https://groceries.morrisons.com/search?q={query}',
    logoUrl: `${process.env.REACT_APP_CDN_URL}/images/morrisons.png`
  }
  ,
  { 
    name: 'Asda',
    searchUrl: 'https://groceries.asda.com/search/{query}',
    logoUrl: `${process.env.REACT_APP_CDN_URL}/images/asda.png`
  },
  { 
    name: 'Amazon Fresh',
    searchUrl: 'https://www.amazon.com/s?k={query}&i=grocery',
    logoUrl: `${process.env.REACT_APP_CDN_URL}/images/amazon.png`
  }
];

// Extract AddItemModal as a separate component outside the main component
const AddItemModal = memo(({ 
  show, 
  onClose, 
  onAddItem, 
  initialItem = { name: '', quantity: '', unit: '', category: 'other' } 
}) => {
  // Use local state within the modal component
  const [itemData, setItemData] = useState(initialItem);

  // Unit options for dropdown
  const unitOptions = [
    { value: '', label: 'None' },
    { value: 'g', label: 'Grams (g)' },
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'lb', label: 'Pounds (lb)' },
    { value: 'oz', label: 'Ounces (oz)' },
    { value: 'ml', label: 'Milliliters (ml)' },
    { value: 'l', label: 'Liters (l)' },
    { value: 'tsp', label: 'Teaspoon (tsp)' },
    { value: 'tbsp', label: 'Tablespoon (tbsp)' },
    { value: 'cup', label: 'Cups' },
    { value: 'pcs', label: 'Pieces' },
    { value: 'cloves', label: 'Cloves' },
    { value: 'bunch', label: 'Bunch' },
    { value: 'pinch', label: 'Pinch' }
  ];

  // Reset form when modal opens - but only once when the show prop changes
  useEffect(() => {
    if (show) {
      setItemData(initialItem);
    }
  }, [show]); // Remove initialItem from dependencies to prevent loops

  const handleChange = (e, field) => {
    let value = e.target.value;
    
    // Only allow numbers for quantity field
    if (field === 'quantity' && value !== '') {
      // Allow only numbers and decimal point
      if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
        return; // Don't update if not a valid number
      }
    }
    
    setItemData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    if (!itemData.name.trim()) return;
    onAddItem(itemData);
  };

  // Don't render anything if not showing
  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-2xl p-7 w-full max-w-md mx-4 shadow-2xl border border-gray-700"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold mb-5 text-white border-b border-gray-700 pb-3">
          Add New Item to Shopping List
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Item Name
            </label>
            <input
              type="text"
              placeholder="e.g. Milk, Apples, Bread"
              value={itemData.name}
              onChange={e => handleChange(e, 'name')}
              className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-soft-pink/30 focus:border-soft-pink shadow-sm"
              autoFocus
            />
          </div>
          
          <div className="flex gap-3">
            <div className="w-1/3">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Quantity
              </label>
              <input
                type="text"
                placeholder="1, 0.5, 2..."
                value={itemData.quantity}
                onChange={e => handleChange(e, 'quantity')}
                className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-soft-pink/30 focus:border-soft-pink shadow-sm"
              />
            </div>
            
            <div className="w-2/3">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Unit
              </label>
              <select
                value={itemData.unit}
                onChange={e => handleChange(e, 'unit')}
                className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-soft-pink/30 focus:border-soft-pink shadow-sm"
              >
                {unitOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Category
            </label>
            <select
              value={itemData.category}
              onChange={e => handleChange(e, 'category')}
              className="w-full px-4 py-3 rounded-lg border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-soft-pink/30 focus:border-soft-pink shadow-sm"
            >
              <option value="produce">Produce</option>
              <option value="dairy">Dairy</option>
              <option value="meat">Meat</option>
              <option value="seafood">Seafood</option>
              <option value="grains">Grains</option>
              <option value="canned">Canned Goods</option>
              <option value="pantry">Pantry</option>
              <option value="spices">Spices & Herbs</option>
              <option value="other">Other Items</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="py-2.5 px-5 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-800 transition-all duration-300"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="py-2.5 px-4 bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 text-pri-dark rounded-full hover:shadow-lg transition-all duration-300"
            disabled={!itemData.name.trim()}
            type="button"
          >
            Add Item
          </button>
        </div>
      </div>
    </div>
  );
});

// Add a new ShopModal component
const ShopModal = memo(({ 
  show, 
  onClose, 
  item,
  stores
}) => {
  if (!show) return null;
  
  const handleStoreSelect = (store) => {
    // Encode the item name for use in URL
    const query = encodeURIComponent(item.name);
    // Replace {query} placeholder in the store's URL template
    const url = store.searchUrl.replace('{query}', query);
    
    window.open(url, '_blank');
    onClose();
  };
  
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-gray-700"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold mb-5 text-white border-b border-gray-700 pb-3 flex items-center">
          <FaShoppingCart className="mr-3 text-blue-400" />
          Shop for "{item.name}"
        </h3>
        
        <p className="text-gray-300 mb-5 text-sm">Select a store to search for this item:</p>
        
        <div className="grid grid-cols-2 gap-5 mb-4">
          {stores.map((store) => (
            <button
              key={store.name}
              onClick={() => handleStoreSelect(store)}
              className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-800 transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-2 shadow-lg hover:scale-105 transition-transform p-2 overflow-hidden">
                <img 
                  src={store.logoUrl} 
                  alt={`${store.name} logo`} 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-white font-medium">{store.name}</span>
            </button>
          ))}
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="py-2 px-5 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-800 transition-all duration-300"
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
});

// ShoppingListItem component for the inline rendering
const ShoppingListItem = memo(({ 
  item, 
  isSelected, 
  onToggle, 
  onEdit, 
  onRemove, 
  displayQuantity 
}) => {
  const [showStoresDropdown, setShowStoresDropdown] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  const openGroceryStore = (e, store) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Encode the item name for use in URL
    const query = encodeURIComponent(item.name);
    // Replace {query} placeholder in the store's URL template
    const url = store.searchUrl.replace('{query}', query);
    
    window.open(url, '_blank');
    setShowStoresDropdown(false);
  };

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowStoresDropdown(!showStoresDropdown);
  };

  // Close dropdown when clicking outside
  const closeDropdown = () => {
    setShowStoresDropdown(false);
  };

  // Handle click outside
  React.useEffect(() => {
    if (showStoresDropdown) {
      document.addEventListener('click', closeDropdown);
      return () => {
        document.removeEventListener('click', closeDropdown);
      };
    }
  }, [showStoresDropdown]);
  
  return (
    <li className="p-3 hover:bg-gray-800/30 flex items-center justify-between transition-all duration-300 border-b border-gray-700/50 last:border-0">
      <div className="flex items-center flex-1">
        <div
          className="cursor-pointer mr-3"
          onClick={(e) => {
            e.preventDefault();
            onToggle(item.id || item._tempId);
          }}
        >
          {isSelected ? (
            <FaCheckCircle className="text-mint-green text-lg" />
          ) : (
            <FaCircle className="text-gray-600 text-lg hover:text-soft-pink/50 transition-colors" />
          )}
        </div>
        <span className={`${isSelected ? 'line-through text-gray-500' : 'text-gray-300'} font-medium`}>
          {item.name}
        </span>
      </div>
      
      <div className="flex items-center">
        <span className="text-gray-300 text-sm bg-gray-800/60 px-2.5 py-1 rounded-full shadow-sm border border-gray-700 mr-2">
          {displayQuantity(item.quantity, item.unit)}
        </span>
        
        {/* Grocery store dropdown */}
        <div className="relative inline-block mr-2">
          <div 
            className="group relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <button 
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown(e);
              }}
              type="button"
              className="p-2 bg-blue-500/20 hover:bg-blue-500/50 border border-blue-500/30 rounded-lg text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1 shadow-md"
              title="Shop online"
            >
              <FaShoppingCart className="text-base" />
              <span className="text-xs font-medium hidden sm:inline">Shop</span>
              <FaChevronDown className="text-[10px]" />
            </button>
            
            {/* Tooltip */}
            <AnimatePresence>
              {showTooltip && !showStoresDropdown && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute -left-20 bottom-full mb-2 w-48 bg-gray-900 text-white text-xs rounded p-2 shadow-lg z-20"
                >
                  <div className="flex items-start">
                    <FaInfoCircle className="text-blue-400 mt-0.5 mr-1.5 flex-shrink-0" />
                    <span>Search for "{item.name}" in online grocery stores</span>
                  </div>
                  <div className="absolute left-24 top-full h-2 w-2 bg-gray-900 transform rotate-45"></div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <AnimatePresence>
            {showStoresDropdown && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: -5 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -5 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="py-1 text-sm font-medium text-white">
                  <div className="px-3 py-1.5 border-b border-gray-700 text-gray-400 text-xs uppercase">
                    Select a store
                  </div>
                  {groceryStores.map((store) => (
                    <button
                      key={store.name}
                      onClick={(e) => openGroceryStore(e, store)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-700 flex items-center text-white"
                    >
                      <div className="w-6 h-6 bg-white rounded-full p-1 mr-2 flex-shrink-0">
                        <img 
                          src={store.logoUrl} 
                          alt={`${store.name} logo`}
                          className="w-full h-full object-contain" 
                        />
                      </div>
                      {store.name}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <button
          onClick={(e) => {
            e.preventDefault();
            onEdit(item);
          }}
          className="p-1.5 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700 rounded-full text-gray-400 hover:text-mint-green transition-colors mr-1"
        >
          <FaEdit />
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            onRemove(item);
          }}
          className="p-1.5 bg-gray-800/60 hover:bg-soft-pink/30 border border-gray-700 rounded-full text-gray-400 hover:text-soft-pink transition-colors"
        >
          <FaTrash />
        </button>
      </div>
    </li>
  );
});

const ShoppingList = () => {
  const dispatch = useDispatch();
  const { shoppingList: reduxShoppingList, mealPlans, status } = useSelector((state) => state.mealPlanner);
  const { user } = useSelector((state) => state.auth);
  const loading = status === 'loading';
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    produce: true,
    dairy: true,
    meat: true,
    seafood: true,
    grains: true,
    canned: true,
    pantry: true,
    spices: true,
    other: true
  });
  
  // Add state for shop dropdown
  const [openShopDropdownId, setOpenShopDropdownId] = useState(null);
  const [tooltipItemId, setTooltipItemId] = useState(null);
  
  // Date Range Selection
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [showGenerateListModal, setShowGenerateListModal] = useState(false);
  
  // Local state for shopping list (to avoid modifying redux state directly)
  const [localShoppingList, setLocalShoppingList] = useState({
    items: [],
    dateRange: reduxShoppingList?.dateRange || { start: "", end: "" }
  });
  
  // Add a specific state to force re-renders
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Use combined shopping list - either from Redux or local state
  const shoppingList = React.useMemo(() => {
    console.log("Redux shopping list structure:", reduxShoppingList);
    
    // Check if Redux shopping list has items
    if (reduxShoppingList?.items) {
      // Handle both array and object formats
      const hasItems = Array.isArray(reduxShoppingList.items) 
        ? reduxShoppingList.items.length > 0 
        : typeof reduxShoppingList.items === 'object' && Object.keys(reduxShoppingList.items).length > 0;
      
      if (hasItems) {
        console.log("Using Redux shopping list data");
        return reduxShoppingList;
      }
    }
    
    // Fall back to local data if Redux is empty
    console.log("Using local shopping list data");
    return localShoppingList;
  }, [reduxShoppingList, localShoppingList]);

  // Fix the initial check for shopping list
  useEffect(() => {
    console.log("Checking if shopping list exists...");
    dispatch(checkShoppingList())
      .then((result) => {
        console.log("checkShoppingList result:", result);
        
        // If shopping list exists, fetch it
        if (result.payload && result.payload.exists) {
          console.log("Shopping list exists, fetching items...");
          
          // Set date range to match the existing list
          if (result.payload.dateRange && result.payload.dateRange.start && result.payload.dateRange.end) {
            const start = parseISO(result.payload.dateRange.start);
            const end = parseISO(result.payload.dateRange.end);
            
            if (isValid(start) && isValid(end)) {
              setDateRangeStart(start);
              setDateRangeEnd(end);
              
              // Fetch the actual shopping list items
              dispatch(fetchShoppingList({
                startDate: result.payload.dateRange.start,
                endDate: result.payload.dateRange.end,
                forceGenerate: false // Make sure we're fetching existing list, not generating a new one
              })).then(fetchResult => {
                console.log("Fetched existing shopping list:", fetchResult);
                
                // Initialize local state from fetched data
                if (fetchResult.payload && fetchResult.payload.items) {
                  setLocalShoppingList({
                    items: fetchResult.payload.items,
                    dateRange: fetchResult.payload.dateRange,
                    listId: fetchResult.payload.listId,
                    exists: true
                  });
                  
                  // This is a saved list, so ensure the banner is not shown
                  setListGeneratedNotSaved(false);
                }
              }).catch(error => {
                console.error("Error fetching existing shopping list:", error);
              });
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error checking shopping list:", error);
      });
  }, [dispatch]);

  // Initialize date range based on available meal plans
  useEffect(() => {
    // Skip if we already have date range set
    if (dateRangeStart && dateRangeEnd) {
      return;
    }
    
    // Get all available dates from meal plans
    const availableDates = Object.keys(mealPlans).sort();
    
    if (availableDates.length > 0) {
      // Use the earliest and latest dates from available meal plans
      const startDate = parseISO(availableDates[0]);
      const endDate = parseISO(availableDates[availableDates.length - 1]);
      
      setDateRangeStart(startDate);
      setDateRangeEnd(endDate);
      
      console.log(`Initializing shopping list date range: ${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`);
      
      // Only fetch if we don't already have a shopping list
      if (!shoppingList.exists && !shoppingList.listId) {
        console.log("No shopping list exists yet, not auto-fetching");
        // Don't auto-fetch, wait for user to click generate
      }
    } else {
      // If no meal plans, use current week
      const today = new Date();
      const startOfWeekDate = startOfWeek(today);
      const endOfWeekDate = endOfWeek(today);
      
      setDateRangeStart(startOfWeekDate);
      setDateRangeEnd(endOfWeekDate);
      
      console.log(`No meal plans found. Using current week: ${format(startOfWeekDate, 'yyyy-MM-dd')} to ${format(endOfWeekDate, 'yyyy-MM-dd')}`);
    }
  }, [dispatch, mealPlans, shoppingList.exists, shoppingList.listId]);

  // Handle date range selection
  const handleDateRangeSelect = (start, end) => {
    console.log(`Date range selected: ${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`);
    
    setDateRangeStart(start);
    setDateRangeEnd(end);
    setShowDateRangePicker(false);
    
    // Clear current shopping list to show loading state
    setLocalShoppingList({
      items: [],
      dateRange: { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') }
    });
    
    // Clear selection when fetching new items
    setSelectedItems(new Set());
    
    // Fetch shopping list for the new date range
    if (start && end) {
      console.log(`Dispatching fetchShoppingList for: ${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`);
      
      // Set loading state manually first
      dispatch({ type: 'mealPlanner/fetchShoppingList/pending' });
      
      // Then dispatch the thunk - by default we don't force generate from date picker changes
      // This allows viewing existing lists when switching dates
      dispatch(fetchShoppingList({
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
        forceGenerate: false // Don't force generate from date picker - allow viewing existing lists
      })).then(result => {
        console.log("fetchShoppingList result:", result);
      }).catch(error => {
        console.error("Error fetching shopping list:", error);
      });
    }
  };

  // Update the fetchShoppingList handler in refreshShoppingList to ensure UI updates
  const refreshShoppingList = () => {
    if (dateRangeStart && dateRangeEnd) {
      console.log(`Refreshing shopping list for: ${format(dateRangeStart, 'yyyy-MM-dd')} to ${format(dateRangeEnd, 'yyyy-MM-dd')}`);
      
      // Clear current shopping list to show loading state
      setLocalShoppingList({
        items: {},
        dateRange: { 
          start: format(dateRangeStart, 'yyyy-MM-dd'), 
          end: format(dateRangeEnd, 'yyyy-MM-dd') 
        }
      });
      
      // Clear selection when refreshing
      setSelectedItems(new Set());
      
      // Set loading state manually first
      dispatch({ type: 'mealPlanner/fetchShoppingList/pending' });
      
      // Then dispatch the thunk with forceGenerate flag to avoid using saved list
      dispatch(fetchShoppingList({
        startDate: format(dateRangeStart, 'yyyy-MM-dd'),
        endDate: format(dateRangeEnd, 'yyyy-MM-dd'),
        forceGenerate: true // Add this flag to force a fresh generation
      })).then(result => {
        console.log("Refresh shopping list result:", result);
        
        // Important: Update local state with the new data to ensure UI refreshes
        if (result.payload && result.payload.items) {
          setLocalShoppingList({
            items: result.payload.items,
            dateRange: result.payload.dateRange || {
              start: format(dateRangeStart, 'yyyy-MM-dd'),
              end: format(dateRangeEnd, 'yyyy-MM-dd')
            },
            listId: result.payload.listId,
            exists: false // This is a newly generated list
          });
          
          // Force UI refresh by resetting other states
          setSelectedItems(new Set());
          setSearchTerm('');
          setFilterCategory('');
          
          // Explicitly expand all categories for the new list
          const newExpandedCategories = {};
          if (typeof result.payload.items === 'object') {
            Object.keys(result.payload.items).forEach(category => {
              newExpandedCategories[category] = true;
            });
            setExpandedCategories(prev => ({...prev, ...newExpandedCategories}));
          }
        }
        
        // Mark as not saved
        setListGeneratedNotSaved(true);
      }).catch(error => {
        console.error("Error refreshing shopping list:", error);
      });
    } else {
      // Fallback to all available dates
      const availableDates = Object.keys(mealPlans).sort();
      if (availableDates.length > 0) {
        console.log(`Falling back to all available dates: ${availableDates[0]} to ${availableDates[availableDates.length - 1]}`);
        
        dispatch(fetchShoppingList({
          startDate: availableDates[0],
          endDate: availableDates[availableDates.length - 1]
        })).then(result => {
          console.log("Fallback shopping list result:", result);
          // Removed automatic saving - user must click Save button
        }).catch(error => {
          console.error("Error fetching fallback shopping list:", error);
        });
      } else {
        console.log("No date range or available dates, using default");
        dispatch(fetchShoppingList()).then(result => {
          console.log("Default shopping list result:", result);
        }).catch(error => {
          console.error("Error fetching default shopping list:", error);
        });
      }
    }
  };

  // Add state to track when a list is generated but not saved
  const [listGeneratedNotSaved, setListGeneratedNotSaved] = useState(false);

  // Ensure listGeneratedNotSaved is false when there's no valid shopping list on mount
  useEffect(() => {
    const hasValidShoppingList = (list) => {
      if (!list?.items) return false;
      if (Array.isArray(list.items)) return list.items.length > 0;
      if (typeof list.items === 'object') {
        return Object.values(list.items).some(items => 
          Array.isArray(items) ? items.length > 0 : Boolean(items)
        );
      }
      return false;
    };
    
    // If neither Redux nor local state has a valid shopping list, ensure banner is hidden
    if (!hasValidShoppingList(reduxShoppingList) && !hasValidShoppingList(localShoppingList)) {
      setListGeneratedNotSaved(false);
    }
  }, []);

  // Add a function to select common date ranges
  const selectDateRange = (type) => {
    const today = new Date();
    let start, end;
    
    switch(type) {
      case 'week':
        start = startOfWeek(today);
        end = endOfWeek(today);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'twoWeeks':
        start = today;
        end = new Date(today);
        end.setDate(today.getDate() + 13); // 14 days total
        break;
      default:
        start = today;
        end = today;
    }
    
    console.log(`Preset date range selected (${type}): ${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`);
    
    setDateRangeStart(start);
    setDateRangeEnd(end);
    
    // Clear current shopping list to show loading state
    setLocalShoppingList({
      items: [],
      dateRange: { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') }
    });
    
    // Clear selection when switching date ranges
    setSelectedItems(new Set());
    
    console.log(`Dispatching fetchShoppingList for preset: ${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`);
    
    // Set loading state manually first
    dispatch({ type: 'mealPlanner/fetchShoppingList/pending' });
    
    // Then dispatch the thunk
    dispatch(fetchShoppingList({
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      forceGenerate: false // Allow displaying existing lists when changing preset date ranges
    })).then(result => {
      console.log("Preset fetchShoppingList result:", result);
    }).catch(error => {
      console.error("Error fetching shopping list for preset:", error);
    });
  };

  // Monitor shopping list updates
  useEffect(() => {
    // Log when the Redux shopping list changes
    console.log("Redux shopping list updated:", reduxShoppingList);
    
    if (reduxShoppingList?.items) {
      if (Array.isArray(reduxShoppingList.items)) {
        console.log("Shopping list contains", reduxShoppingList.items.length, "items (array format)");
      } else if (typeof reduxShoppingList.items === 'object') {
        console.log("Shopping list contains", Object.keys(reduxShoppingList.items).length, 
          "categories with", Object.values(reduxShoppingList.items).flat().length, "total items");
      }
      
      // Log the date range
      if (reduxShoppingList.dateRange) {
        console.log("Shopping list date range:", reduxShoppingList.dateRange);
      }
      
      // Only set listGeneratedNotSaved to true if there are actual items in the list
      // and the list was newly generated (not from saved)
      const hasItems = Array.isArray(reduxShoppingList.items) ? 
        reduxShoppingList.items.length > 0 : 
        Object.values(reduxShoppingList.items).some(items => 
          Array.isArray(items) ? items.length > 0 : Boolean(items)
        );
      
      if (hasItems && !reduxShoppingList.fromSaved && !reduxShoppingList.listId) {
        setListGeneratedNotSaved(true);
      } else {
        setListGeneratedNotSaved(false);
      }
    } else {
      console.log("Shopping list update detected but no items array");
      // No items in the list, so definitely not generated and unsaved
      setListGeneratedNotSaved(false);
    }
  }, [reduxShoppingList]);

  const toggleItem = (itemId) => {
    // Ensure the itemId is not undefined
    if (!itemId) {
      console.error("Tried to toggle item with undefined ID");
      return;
    }
    
    console.log("Toggling item:", itemId);
    
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId);
    } else {
      newSelectedItems.add(itemId);
    }
    setSelectedItems(newSelectedItems);
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
    }, 0);
  };

  const selectAllItems = () => {
    if (filteredGroupedItems.length === 0) return;
    
    const allItemIds = [];
    filteredGroupedItems.forEach(group => {
      group.items.forEach(item => {
        // Use _tempId as the primary identifier, falling back to id if _tempId is not available
        const itemId = item._tempId || item.id;
        if (itemId) {
          allItemIds.push(itemId);
        }
      });
    });
    
    if (allItemIds.length > 0 && allItemIds.every(id => selectedItems.has(id))) {
      // If all are selected, unselect all
      setSelectedItems(new Set());
    } else {
      // Otherwise, select all
      setSelectedItems(new Set(allItemIds));
    }
  };

  // Helper function to normalize item names for comparison
  const normalizeItemName = (name) => {
    return name;  // No normalization needed as it's handled by API
  };
  
  // Normalize unit for comparison
  const normalizeUnit = (unit) => {
    return unit;  // No normalization needed as it's handled by API
  };

  // Helper function to extract numeric quantity and unit
  const parseQuantity = (quantityStr) => {
    if (typeof quantityStr === 'number') return quantityStr;
    if (!quantityStr) return 0;
    
    // Just convert to float, no complex parsing needed
    const numericValue = parseFloat(quantityStr);
    return isNaN(numericValue) ? 0 : numericValue;
  };
  
  // Format quantity with unit properly
  const formatQuantity = (amount, unit) => {
    if (amount === 0 || !amount) return '';
    return `${amount} ${unit || ''}`.trim();
  };

  // Update the displayQuantity function to properly show units from the API
  const displayQuantity = (quantity, unit) => {
    if (!quantity && quantity !== 0) return '';
    return `${quantity} ${unit || ''}`.trim();
  };

  const printShoppingList = () => {
    const dateRangeText = dateRangeStart && dateRangeEnd
      ? `${format(dateRangeStart, 'MMM d, yyyy')} - ${format(dateRangeEnd, 'MMM d, yyyy')}`
      : 'All dates';

    const printWindow = window.open('', '_blank');
    const printContent = PrintTemplate({ 
      dateRangeText, 
      groupedItems: shoppingList.items || {} 
    });
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Update groupItemsByCategory to better handle database data
  const groupItemsByCategory = React.useMemo(() => {
    // First determine which shopping list to use - prefer Redux state over local state
    const currentList = shoppingList && Object.keys(shoppingList.items || {}).length > 0 
      ? shoppingList 
      : localShoppingList;
    
    console.log("CURRENT LIST FOR GROUPING:", currentList);
    
    if (!currentList?.items || Object.keys(currentList.items || {}).length === 0) {
      console.log("No items found in shopping list (either Redux or local)");
      return [];
    }
    
    console.log("Grouping items from:", currentList === shoppingList ? "Redux state" : "Local state");
    console.log("Items structure:", currentList.items);
    
    // Handle case where items is already grouped by categories
    if (!Array.isArray(currentList.items) && typeof currentList.items === 'object') {
      const result = [];
      
      // Convert each category into the expected format
      for (const [category, categoryItems] of Object.entries(currentList.items)) {
        // Skip empty categories or non-array items
        if (!categoryItems) continue;
        
        // Convert to array if it's not already
        const itemsArray = Array.isArray(categoryItems) ? categoryItems : [categoryItems];
        
        if (itemsArray.length > 0) {
          console.log(`Category ${category} has ${itemsArray.length} items`);
          // Add ID to each item and preserve unit field
          const items = itemsArray.map((item, index) => {
            if (!item) return null;
            
            // Generate a more unique, deterministic temporary ID for rendering purposes
            // Use all available identifying properties to create a unique string
            const nameStr = item.name || 'unknown';
            const quantityStr = item.quantity !== undefined ? item.quantity.toString() : '';
            const unitStr = item.unit || '';
            const idStr = item.id || '';
            
            // Create a deterministic temporary ID based on item properties
            const tempId = `${category}-${index}-${nameStr}-${quantityStr}-${unitStr}-${idStr}`;
            
            return {
              ...item,
              _tempId: tempId, // Use _tempId for React keys without modifying the actual data
              id: item.id || tempId // Ensure every item has a unique ID - use existing or tempId
            };
          }).filter(Boolean); // Remove any null items
          
          if (items.length > 0) {
            result.push({
              category,
              items
            });
          }
        }
      }
      
      console.log("Grouped items result:", result);
      return result.sort((a, b) => a.category.localeCompare(b.category));
    }
    
    // Rest of the existing function...
    return []; // Return empty by default
  }, [
    shoppingList?.items ? JSON.stringify(Object.keys(shoppingList.items)) : '', 
    localShoppingList?.items ? JSON.stringify(Object.keys(localShoppingList.items)) : '',
    localShoppingList?._timestamp,
    shoppingList?._timestamp,
    refreshKey
  ]);

  // Get all unique categories for filtering
  const categories = React.useMemo(() => {
    if (!shoppingList?.items) return [];
    
    // Build a set of unique categories from all items
    const uniqueCategories = new Set();
    
    if (Array.isArray(shoppingList.items)) {
      // If items is an array, extract categories from each item
      shoppingList.items.forEach(item => {
        uniqueCategories.add(item.category || 'other');
      });
    } else if (typeof shoppingList.items === 'object') {
      // If items is an object with categories, use the keys and also check items
      Object.entries(shoppingList.items).forEach(([category, items]) => {
        uniqueCategories.add(category);
        
        // Also check if items have their own categories
        if (Array.isArray(items)) {
          items.forEach(item => {
            if (item.category && item.category !== category) {
              uniqueCategories.add(item.category);
            }
          });
        }
      });
    }
    
    // Convert Set to array and sort
    return ['', ...Array.from(uniqueCategories)].sort();
  }, [shoppingList.items]);

  // Move the getTotalItems function above filteredGroupedItems
  // Update the getTotalItems function to handle redux state better
  const getTotalItems = () => {
    // Count the total number of items across all categories
    if (!shoppingList?.items && !localShoppingList?.items) return 0;
    
    // Use redux state as the source of truth if it has items
    const items = shoppingList?.items && Object.keys(shoppingList.items).length > 0
      ? shoppingList.items
      : localShoppingList?.items;
    
    console.log("Getting total items from:", items);
    
    // If items is an array, just return its length
    if (Array.isArray(items)) {
      return items.length;
    }
    
    // If items is grouped by categories, count each category's items
    if (typeof items === 'object') {
      let total = 0;
      for (const categoryItems of Object.values(items)) {
        if (Array.isArray(categoryItems)) {
          total += categoryItems.length;
        } else if (categoryItems && typeof categoryItems === 'object') {
          // Single item as an object
          total += 1;
        }
      }
      return total;
    }
    
    return 0; // Default return value
  };

  // Add a key dependency to filteredGroupedItems to ensure it recalculates when items change
  const filteredGroupedItems = React.useMemo(() => {
    console.log("Computing filteredGroupedItems");
    
    // First get all grouped items
    const groupedItems = groupItemsByCategory;
    
    console.log("Grouped items before filtering:", groupedItems);
    
    if (!groupedItems || groupedItems.length === 0) {
      console.log("No grouped items found");
      return [];
    }
    
    // If no filtering is applied, return all items
    if (searchTerm === '' && filterCategory === '') {
      console.log(`Returning all ${groupedItems.length} groups without filtering`);
      return groupedItems;
    }
    
    // Apply filters
    const filtered = groupedItems
      .map(group => {
        // Skip this group if it doesn't match the category filter
        if (filterCategory !== '' && group.category !== filterCategory) {
          return null;
        }
        
        // If no search term, keep all items in this group
        if (searchTerm === '') {
          return group;
        }
        
        // Filter items in the group by search term
        const filteredItems = group.items.filter(item => 
          item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        // Only keep the group if it has matching items
        return filteredItems.length > 0 
          ? { ...group, items: filteredItems } 
          : null;
      })
      .filter(Boolean); // Remove null groups
      
    console.log("Filtered items result:", filtered);
    return filtered;
  }, [
    searchTerm, 
    filterCategory, 
    // Include these dependencies to force recalculation when shopping list changes
    shoppingList?.items ? JSON.stringify(Object.keys(shoppingList.items)) : '', 
    localShoppingList?.items ? JSON.stringify(Object.keys(localShoppingList.items)) : '',
    localShoppingList?._timestamp, // Add timestamp to force recalculation
    shoppingList?._timestamp,
    refreshKey // Add refreshKey to force recalculation when items are removed
  ]);

  const allSelected = filteredGroupedItems.length > 0 && 
    filteredGroupedItems.every(group => 
      group.items.every(item => selectedItems.has(item.id))
    );

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Categories with readable names
  const categoryNames = {
    produce: 'Produce',
    dairy: 'Dairy & Eggs',
    meat: 'Meat',
    seafood: 'Seafood',
    grains: 'Grains & Bread',
    canned: 'Canned Goods',
    pantry: 'Pantry Staples',
    spices: 'Spices & Herbs',
    other: 'Other Items'
  };

  // Fix the hasItems check to properly check if a list exists
  const hasItems = React.useMemo(() => {
    // Check for items in redux state first
    if (shoppingList?.items) {
      if (Array.isArray(shoppingList.items) && shoppingList.items.length > 0) {
        return true;
      }
      if (typeof shoppingList.items === 'object' && 
          Object.values(shoppingList.items).some(category => 
            Array.isArray(category) ? category.length > 0 : Boolean(category))) {
        return true;
      }
    }
    
    // Check localShoppingList as fallback
    if (localShoppingList?.items) {
      if (Array.isArray(localShoppingList.items) && localShoppingList.items.length > 0) {
        return true;
      }
      if (typeof localShoppingList.items === 'object' && 
          Object.values(localShoppingList.items).some(category => 
            Array.isArray(category) ? category.length > 0 : Boolean(category))) {
        return true;
      }
    }
    
    // Or if a shopping list exists in the database but items haven't been fetched yet
    return shoppingList?.exists === true || localShoppingList?.exists === true;
  }, [shoppingList, localShoppingList]);

  // Fix the showEmptyState logic to consider the exists flag
  const showEmptyState = React.useMemo(() => {
    // Only show empty state if we're not loading and there are truly no items
    if (loading) return false;
    
    // Check if we have items in the filtered list
    if (filteredGroupedItems && filteredGroupedItems.length > 0) {
      return false;
    }
    
    // Check if we have a "not found" status from Redux
    if (reduxShoppingList?.status === 'not_found') {
      return true;
    }
    
    // Check if we have an explicit message about no list
    if (reduxShoppingList?.message && reduxShoppingList.message.includes('No shopping list found')) {
      return true;
    }
    
    // Final check - if we have no items and no list exists flag
    return !hasItems;
  }, [loading, filteredGroupedItems, reduxShoppingList, hasItems]);

  // UI elements for empty state
  const EmptyState = () => {
    // Get date range from meal plans
    const availableDates = Object.keys(mealPlans).sort();
    const startDate = availableDates.length > 0 ? availableDates[0] : null;
    const endDate = availableDates.length > 0 ? availableDates[availableDates.length - 1] : null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-2xl p-10 text-center border border-gray-700 shadow-xl"
      >
        <div className="mx-auto w-24 h-24 flex items-center justify-center bg-gradient-to-r from-soft-pink to-mint-green rounded-full mb-5 shadow-lg transform hover:scale-105 transition-transform duration-300">
          <FaShoppingBasket className="text-pri-dark text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">No Shopping List Available</h3>
        <p className="text-gray-300 mb-7 max-w-md mx-auto leading-relaxed">
          {Object.keys(mealPlans).length > 0 
            ? "Click the button below to generate your shopping list based on your meal plans."
            : "Generate a meal plan first to create your shopping list."
          }
        </p>
        {Object.keys(mealPlans).length > 0 ? (
        <button
            onClick={() => setShowGenerateListModal(true)}
            className="bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 text-pri-dark font-medium py-3.5 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Generate Shopping List
        </button>
        ) : (
          <p className="text-gray-400 italic">
            You need to create meal plans before generating a shopping list.
          </p>
        )}
      </motion.div>
    );
  };

  // Generate shopping list modal
  const GenerateShoppingListModal = () => {
    // Select preset date range
    const [modalRangeType, setModalRangeType] = useState('week');
    const [modalStartDate, setModalStartDate] = useState(null);
    const [modalEndDate, setModalEndDate] = useState(null);
    const [showMiniCalendar, setShowMiniCalendar] = useState(false);
    
    // Initialize with current week
    useEffect(() => {
      const today = new Date();
      const start = startOfWeek(today);
      const end = endOfWeek(today);
      
      setModalStartDate(start);
      setModalEndDate(end);
    }, []);
    
    // Update dates when range type changes
    useEffect(() => {
      const today = new Date();
      let start, end;
      
      switch(modalRangeType) {
        case 'week':
          start = startOfWeek(today);
          end = endOfWeek(today);
          break;
        case 'twoWeeks':
          start = today;
          end = new Date(today);
          end.setDate(today.getDate() + 13); // 14 days total
          break;
        case 'custom':
          // Keep current custom selection
          setShowMiniCalendar(true);
          return;
        default:
          start = today;
          end = today;
      }
      
      setModalStartDate(start);
      setModalEndDate(end);
      setShowMiniCalendar(false);
    }, [modalRangeType]);
    
    // Handle Mini Calendar date selection
    const handleDateRangeSelect = (start, end) => {
      console.log("Date range selected in MiniCalendar:", start, end);
      
      // Update the date range
      setModalStartDate(start);
      setModalEndDate(end);
      
      // Hide the calendar and show confirmation buttons
      setShowMiniCalendar(false);
    };
    
    // Generate shopping list - updated to refresh UI properly
    const handleGenerateList = () => {
      if (modalStartDate && modalEndDate) {
        // Format dates
        const startFormatted = format(modalStartDate, 'yyyy-MM-dd');
        const endFormatted = format(modalEndDate, 'yyyy-MM-dd');
        
        console.log(`Generating shopping list for: ${startFormatted} to ${endFormatted}`);
        
        // Set the component's date range state
        setDateRangeStart(modalStartDate);
        setDateRangeEnd(modalEndDate);
        
        // Clear current shopping list to show loading state
        setLocalShoppingList({
          items: {},
          dateRange: { 
            start: startFormatted, 
            end: endFormatted 
          }
        });
        
        // Clear selection when switching date ranges
        setSelectedItems(new Set());
        
        // Dispatch thunk action with forceGenerate flag
        dispatch(fetchShoppingList({
          startDate: startFormatted,
          endDate: endFormatted,
          forceGenerate: true // Force generate new list even if one exists
        })).then(result => {
          console.log("Generated shopping list result:", result);
          
          // Important: Update local state with the new data to ensure UI refreshes
          if (result.payload && result.payload.items) {
            // Check if the generated list actually has items
            const hasItems = Array.isArray(result.payload.items) ? 
              result.payload.items.length > 0 : 
              Object.values(result.payload.items).some(items => 
                Array.isArray(items) ? items.length > 0 : Boolean(items)
              );
            
            setLocalShoppingList({
              items: result.payload.items,
              dateRange: result.payload.dateRange || {
                start: startFormatted,
                end: endFormatted
              },
              listId: result.payload.listId,
              exists: false // This is a newly generated list
            });
            
            // Force UI refresh by resetting other states
            setSelectedItems(new Set());
            setSearchTerm('');
            setFilterCategory('');
            
            // Explicitly expand all categories for the new list
            const newExpandedCategories = {};
            if (typeof result.payload.items === 'object') {
              Object.keys(result.payload.items).forEach(category => {
                newExpandedCategories[category] = true;
              });
              setExpandedCategories(prev => ({...prev, ...newExpandedCategories}));
            }
            
            // Only mark as not saved if there are actual items
            if (hasItems) {
              setListGeneratedNotSaved(true);
            } else {
              setListGeneratedNotSaved(false);
            }
          }
        });
        
        // Close modal
        setShowGenerateListModal(false);
      }
    };
    
    return (
      <AnimatePresence>
        {showGenerateListModal && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowGenerateListModal(false)}
          >
            <motion.div
              className="bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-2xl p-7 w-full max-w-md mx-4 shadow-2xl border border-gray-700"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-5 text-white border-b border-gray-700 pb-3">
                Generate Shopping List
              </h3>
              
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Date Range Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    className={`py-2.5 px-3 rounded-full text-sm font-medium transition-all duration-300 ${
                      modalRangeType === 'week' 
                        ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark shadow-md' 
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                    onClick={() => setModalRangeType('week')}
                  >
                    Current Week
                  </button>
                  <button
                    className={`py-2.5 px-3 rounded-full text-sm font-medium transition-all duration-300 ${
                      modalRangeType === 'twoWeeks' 
                        ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark shadow-md' 
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                    onClick={() => setModalRangeType('twoWeeks')}
                  >
                    Next 14 Days
                  </button>
                  <button
                    className={`py-2.5 px-3 rounded-full text-sm font-medium transition-all duration-300 ${
                      modalRangeType === 'custom' 
                        ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark shadow-md' 
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                    onClick={() => {
                      setModalRangeType('custom');
                      setShowMiniCalendar(true);
                    }}
                  >
                    Custom
                  </button>
                </div>
              </div>
              
              {/* Custom date selection */}
              {modalRangeType === 'custom' && (
                <>
                  {showMiniCalendar ? (
                    // Show the calendar when in selection mode
                    <div className="mb-5 flex justify-center" onClick={e => e.stopPropagation()}>
                      <MiniCalendar
                        onSelectRange={handleDateRangeSelect}
                        onClose={() => {
                          // If we have dates, just hide the calendar
                          if (modalStartDate && modalEndDate) {
                            setShowMiniCalendar(false);
                          } else {
                            // If no dates selected, revert to week option
                            setModalRangeType('week');
                          }
                        }}
                        initialStartDate={modalStartDate || new Date()}
                        initialEndDate={modalEndDate || new Date()}
                        allowLongRanges={true}
                        preventPastDates={false}
                        maxRangeDays={60}
                      />
                    </div>
                  ) : modalStartDate && modalEndDate ? (
                    // Show selected date range info when dates are selected
                    <>
                      <div className="text-sm text-gray-300 mb-6 bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                        <p>
                          Your shopping list will be generated for the period from{' '}
                          <span className="font-semibold text-white">
                            {format(modalStartDate, 'MMMM d, yyyy')}
                          </span>{' '}
                          to{' '}
                          <span className="font-semibold text-white">
                            {format(modalEndDate, 'MMMM d, yyyy')}
                          </span>
                          <span className="ml-2 text-xs bg-gray-700 px-2 py-0.5 rounded-full text-gray-300">
                            {Math.round((modalEndDate.getTime() - modalStartDate.getTime()) / (1000 * 60 * 60 * 24) + 1)} days
                          </span>
                        </p>
                      </div>
                      
                      <div className="flex justify-center mb-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMiniCalendar(true);
                          }}
                          className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-full text-gray-300 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow border border-gray-700"
                        >
                          <FaCalendarAlt className="text-soft-pink" />
                          <span>Change Dates</span>
                        </button>
                      </div>
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowGenerateListModal(false);
                          }}
                          className="py-2.5 px-5 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-800 transition-all duration-300"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateList();
                          }}
                          className="py-2.5 px-5 bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 text-pri-dark rounded-full hover:shadow-lg transition-all duration-300"
                        >
                          Generate List
                        </button>
                      </div>
                    </>
                  ) : (
                    // Show a button to trigger calendar if no dates selected yet
                    <div className="flex justify-center my-8">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMiniCalendar(true);
                        }}
                        className="flex items-center gap-2 bg-gradient-to-r from-soft-pink to-mint-green px-4 py-2.5 rounded-full text-pri-dark text-sm font-medium transition-all duration-300 shadow-md hover:shadow"
                      >
                        <FaCalendarAlt className="text-pri-dark" />
                        <span>Select Custom Dates</span>
                      </button>
                    </div>
                  )}
                </>
              )}
              
              {/* Standard date range info for non-custom options */}
              {modalRangeType !== 'custom' && modalStartDate && modalEndDate && (
                <>
                  <div className="text-sm text-gray-300 mb-6 bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                    <p>
                      Your shopping list will be generated for the period from{' '}
                      <span className="font-semibold text-white">
                        {format(modalStartDate, 'MMMM d, yyyy')}
                      </span>{' '}
                      to{' '}
                      <span className="font-semibold text-white">
                        {format(modalEndDate, 'MMMM d, yyyy')}
                      </span>
                      <span className="ml-2 text-xs bg-gray-700 px-2 py-0.5 rounded-full text-gray-300">
                        {Math.round((modalEndDate.getTime() - modalStartDate.getTime()) / (1000 * 60 * 60 * 24) + 1)} days
                      </span>
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowGenerateListModal(false);
                      }}
                      className="py-2.5 px-5 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-800 transition-all duration-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateList();
                      }}
                      className="py-2.5 px-5 bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 text-pri-dark rounded-full hover:shadow-lg transition-all duration-300"
                    >
                      Generate List
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // When user saves the list, update the flag
  const saveCurrentShoppingList = () => {
    if (shoppingList?.items && Object.keys(shoppingList.items).length > 0) {
      console.log("Saving current shopping list to database...");
      
      dispatch(saveShoppingList({
        items: shoppingList.items,
        dateRange: shoppingList.dateRange
      })).then(() => {
        setListGeneratedNotSaved(false);
      });
    }
  };

  // Let's add a notification component for unsaved lists
  const UnsavedListNotification = () => {
    if (!listGeneratedNotSaved) return null;
    
    return (
      <div className="bg-gradient-to-r from-amber-400/20 to-amber-500/20 border border-amber-500/50 p-3 rounded-lg mb-4 animate-fadeIn">
        <div className="flex items-center">
          <FaExclamationTriangle className="text-amber-400 mr-3 text-lg flex-shrink-0" />
          <div className="flex-grow text-gray-200 text-sm">
            <p>Your shopping list has been generated but not saved yet. Click "Save" to store it in your account.</p>
          </div>
          <button 
            onClick={saveCurrentShoppingList}
            className="ml-3 bg-amber-500 hover:bg-amber-400 text-pri-dark text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-300 flex-shrink-0 flex items-center"
          >
            <FaSave className="mr-1.5" />
            Save Now
          </button>
        </div>
      </div>
    );
  };

  // Duplicates warning banner
  const DuplicatesWarningBanner = () => {
    // Only show if we have items
    if (!hasItems) return null;
    
    return (
      <div className="bg-gradient-to-r from-blue-400/20 to-blue-500/20 border border-blue-500/50 p-1 rounded-lg mb-4 animate-fadeIn">
        <div className="flex items-center">
          <FaInfoCircle className="text-blue-400 mr-3 text-lg flex-shrink-0" />
          <div className="flex-grow text-pri-gray text-sm">
            <p>We make an effort to remove duplicated items, but there might still be some duplicates. You can remove any unwanted items.</p>
          </div>
        </div>
      </div>
    );
  };

  // Add state for item editing and creation
  const [editingItem, setEditingItem] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  
  // Add a callback for adding a new item
  const addNewItem = useCallback((newItemData) => {
    if (!newItemData.name.trim()) return;
    
    console.log("Adding new item:", newItemData);
    
    // Format the new item without adding an id
    const formattedItem = {
      // Remove id from here
      name: newItemData.name.trim(),
      quantity: newItemData.quantity,
      unit: newItemData.unit,
      category: newItemData.category
    };
    
    console.log("Current shopping list structure:", shoppingList);
    
    // Create a deep copy of the current shopping list items
    let updatedItems = {};
    
    // If shopping list items exist, copy each category carefully
    if (shoppingList && shoppingList.items) {
      if (Array.isArray(shoppingList.items)) {
        // If items is an array, convert to category structure first
        const categorized = {};
        shoppingList.items.forEach(item => {
          const cat = item.category || 'other';
          if (!categorized[cat]) categorized[cat] = [];
          categorized[cat].push({...item});
        });
        updatedItems = categorized;
      } else if (typeof shoppingList.items === 'object') {
        // Handle object-based structure (categories as keys)
        Object.keys(shoppingList.items).forEach(category => {
          if (Array.isArray(shoppingList.items[category])) {
            updatedItems[category] = [...shoppingList.items[category]];
          } else if (shoppingList.items[category]) {
            updatedItems[category] = [{...shoppingList.items[category]}];
          }
        });
      }
    } else if (localShoppingList && localShoppingList.items) {
      // If Redux state is empty, try using local state
      if (typeof localShoppingList.items === 'object') {
        Object.keys(localShoppingList.items).forEach(category => {
          if (Array.isArray(localShoppingList.items[category])) {
            updatedItems[category] = [...localShoppingList.items[category]];
          } else if (localShoppingList.items[category]) {
            updatedItems[category] = Array.isArray(localShoppingList.items[category])
              ? [...localShoppingList.items[category]]
              : [localShoppingList.items[category]];
          }
        });
      }
    }
    
    // Ensure the target category exists as an array
    if (!updatedItems[newItemData.category]) {
      updatedItems[newItemData.category] = [];
    }
    
    // Add the new item
    updatedItems[newItemData.category].push(formattedItem);
    
    console.log("Updated items structure:", updatedItems);
    console.log("Number of categories:", Object.keys(updatedItems).length);
    Object.keys(updatedItems).forEach(category => {
      console.log(`Category ${category} has ${updatedItems[category].length} items`);
    });
    
    // Important: Use a timestamp to force state update
    const timestamp = Date.now();
    
    // Update local shopping list state
    setLocalShoppingList(prevState => {
      console.log("Previous local state:", prevState);
      const newState = {
        ...prevState,
        items: updatedItems,
        dateRange: prevState.dateRange || shoppingList.dateRange || { 
          start: dateRangeStart ? format(dateRangeStart, 'yyyy-MM-dd') : '', 
          end: dateRangeEnd ? format(dateRangeEnd, 'yyyy-MM-dd') : '' 
        },
        _timestamp: timestamp // Add timestamp to force state change detection
      };
      console.log("New local state:", newState);
      
      // Immediately update Redux view to ensure UI sync using the imported action
      dispatch(updateShoppingListView({
        ...newState,
        _timestamp: timestamp
      }));
      
      return newState;
    });
    
    // Make sure the category for the new item is expanded
    setExpandedCategories(prev => ({
      ...prev,
      [newItemData.category]: true
    }));
    
    // Mark as generated but not saved
    setListGeneratedNotSaved(true);
    
    // Close the modal
    setShowAddItemModal(false);
    
    // Force a re-render by setting a temporary state
    setSelectedItems(new Set());
    
    // Add a small delay to ensure UI update
    setTimeout(() => {
      dispatch(updateShoppingListView({ 
        items: updatedItems,
        _timestamp: timestamp + 1 // New timestamp to force another update
      }));
    }, 100);
  }, [setShowAddItemModal, setListGeneratedNotSaved, shoppingList, localShoppingList, dateRangeStart, dateRangeEnd, dispatch]);

  // Function to handle editing an item
  const startEditingItem = (item) => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    setEditingItem({
      ...item,
      tempName: item.name,
      tempQuantity: item.quantity,
      tempUnit: item.unit || '',
      _originalQuantity: item.quantity, // Store this for matching when saving
      _originalName: item.name, // Store this for matching when saving
    });
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
    }, 0);
  };
  
  // Function to save edited item
  const saveEditedItem = () => {
    if (!editingItem) return;
    
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    console.log("Saving edited item:", editingItem);
    
    // Create a deep copy of the current shopping list items
    let updatedItems = {};
    
    // Determine which shopping list data to use (Redux or local)
    const currentList = shoppingList?.items && Object.keys(shoppingList.items).length > 0 
      ? shoppingList 
      : localShoppingList;
    
    if (!currentList?.items) {
      console.error("No shopping list data available to update");
      return;
    }
    
    // Copy the existing structure
    if (typeof currentList.items === 'object') {
      Object.keys(currentList.items).forEach(category => {
        if (Array.isArray(currentList.items[category])) {
          updatedItems[category] = [...currentList.items[category]];
        } else if (currentList.items[category]) {
          updatedItems[category] = Array.isArray(currentList.items[category]) 
            ? [...currentList.items[category]] 
            : [currentList.items[category]];
        }
      });
    }
    
    // Find and update the item
    const category = editingItem.category;
    if (updatedItems[category] && Array.isArray(updatedItems[category])) {
      // More precise identification - use temp ID if available, otherwise match multiple fields
      const itemIndex = updatedItems[category].findIndex(i => {
        // First check if we have an exact match on the _tempId (which is most reliable)
        if (editingItem._tempId && i._tempId === editingItem._tempId) {
          return true;
        }
        
        // Check for ID match if both items have it
        if (editingItem.id && i.id && i.id === editingItem.id) {
          return true;
        }
        
        // If we don't have IDs, match on the original values stored when editing began
        if (i.name === editingItem._originalName) {
          // Try matching on original quantity too for more precision
          if (editingItem._originalQuantity !== undefined && i.quantity !== undefined) {
            // For numeric quantities, perform approximate comparison
            if (typeof editingItem._originalQuantity === 'number' && typeof i.quantity === 'number') {
              return Math.abs(i.quantity - editingItem._originalQuantity) < 0.001;
            }
            // For string quantities, compare directly
            return i.quantity === editingItem._originalQuantity;
          }
          
          // If no other distinguishing factors, just match on name
          return true;
        }
        
        return false;
      });
      
      if (itemIndex !== -1) {
        // Update the item values
        updatedItems[category][itemIndex] = {
          ...updatedItems[category][itemIndex],
          name: editingItem.tempName, // Update the name
          quantity: editingItem.tempQuantity,
          unit: editingItem.tempUnit
        };
        
        console.log(`Updated item at index ${itemIndex} in ${category}`, updatedItems[category][itemIndex]);
      } else {
        console.warn("Could not find item to update");
      }
    }
    
    // Use a timestamp to force state update
    const timestamp = Date.now();
    
    // Update local shopping list state
    setLocalShoppingList(prevState => {
      const newState = {
        ...prevState,
        items: updatedItems,
        _timestamp: timestamp
      };
      
      // Immediately update Redux view to ensure UI sync
      dispatch(updateShoppingListView({
        ...newState,
        _timestamp: timestamp
      }));
      
      return newState;
    });
    
    // Mark as generated but not saved
    setListGeneratedNotSaved(true);
    
    // Clear editing state
    setEditingItem(null);
    
    // Force UI refresh
    setRefreshKey(prev => prev + 1);
    
    // Add a setTimeout to ensure the UI updates and scroll position is maintained
    setTimeout(() => {
      console.log("Forcing UI update after edit");
      setRefreshKey(prev => prev + 1);
      
      // Force a Redux update after a delay
      dispatch(updateShoppingListView({
        items: updatedItems,
        _timestamp: timestamp + 1
      }));
      
      // Restore scroll position
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
    }, 100);
  };
  
  // Function to remove an item from the shopping list
  const removeItem = (item) => {
    if (!item || !item.category || !item.name) {
      console.error("Invalid item data for removal", item);
      return;
    }
    
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    console.log("Removing item:", item);
    
    // Create a deep copy of the current shopping list items
    let updatedItems = {};
    
    // Determine which shopping list data to use (Redux or local)
    const currentList = shoppingList?.items && Object.keys(shoppingList.items).length > 0 
      ? shoppingList 
      : localShoppingList;
    
    if (!currentList?.items) {
      console.error("No shopping list data available to update");
      return;
    }
    
    console.log("Current items before removal:", currentList.items);
    
    // Copy the existing structure
    if (typeof currentList.items === 'object') {
      Object.keys(currentList.items).forEach(category => {
        if (Array.isArray(currentList.items[category])) {
          updatedItems[category] = [...currentList.items[category]];
        } else if (currentList.items[category]) {
          updatedItems[category] = Array.isArray(currentList.items[category]) 
            ? [...currentList.items[category]] 
            : [currentList.items[category]];
        }
      });
    }
    
    // Find and remove the item
    const category = item.category;
    if (updatedItems[category] && Array.isArray(updatedItems[category])) {
      // More precise identification - use temporary ID if available, otherwise match multiple fields
      const itemIndex = updatedItems[category].findIndex(i => {
        // First check if we have an exact match on the _tempId (which is most reliable)
        if (item._tempId && i._tempId === item._tempId) {
          return true;
        }
        
        // Check for ID match if both items have it
        if (item.id && i.id && item.id === i.id) {
          return true;
        }
        
        // If we don't have IDs, match on multiple fields for higher confidence
        // This handles the case where multiple items might have the same name but different quantities
        if (i.name === item.name) {
          // If quantity is defined, match on that too for more precision
          if (item.quantity !== undefined && i.quantity !== undefined) {
            // For numeric quantities, perform approximate comparison to handle floating point issues
            if (typeof item.quantity === 'number' && typeof i.quantity === 'number') {
              return Math.abs(i.quantity - item.quantity) < 0.001;
            }
            // For string quantities, compare directly
            return i.quantity === item.quantity;
          }
          
          // If units are available, match on that too
          if (item.unit && i.unit && item.unit === i.unit) {
            return true;
          }
          
          // If no other distinguishing factors, just match on name
          return true;
        }
        
        return false;
      });
      
      if (itemIndex !== -1) {
        // Remove the item
        updatedItems[category].splice(itemIndex, 1);
        console.log(`Removed item at index ${itemIndex} from ${category}`);
        console.log("Updated items after removal:", updatedItems);
      } else {
        console.warn(`Could not find item to remove in category ${category}`);
        console.warn("Item details:", {
          name: item.name,
          quantity: item.quantity,
          unit: item.unit,
          id: item.id,
          _tempId: item._tempId
        });
      }
    } else {
      console.warn(`Category ${category} not found or not an array`);
    }
    
    // Use a timestamp to force state update
    const timestamp = Date.now();
    
    console.log("Updating local shopping list with new items");
    
    // Update local shopping list state
    setLocalShoppingList(prevState => {
      const newState = {
        ...prevState,
        items: updatedItems,
        _timestamp: timestamp
      };
      
      // Immediately update Redux view to ensure UI sync
      dispatch(updateShoppingListView({
        ...newState,
        _timestamp: timestamp
      }));
      
      console.log("New local state:", newState);
      return newState;
    });
    
    // Force UI refresh
    setRefreshKey(prev => {
      const newKey = prev + 1;
      console.log(`Incremented refresh key to ${newKey}`);
      return newKey;
    });
    
    // Force selected items to reset for UI refresh
    setSelectedItems(new Set());
    
    // Mark as generated but not saved
    setListGeneratedNotSaved(true);
    
    // Add a setTimeout to ensure the UI updates
    setTimeout(() => {
      console.log("Forcing UI update after timeout");
      setRefreshKey(prev => prev + 1);
      
      // Force a Redux update after a delay
      dispatch(updateShoppingListView({
        items: updatedItems,
        _timestamp: timestamp + 1
      }));
      
      // Restore scroll position
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
    }, 100);
  };
  
  // Function to cancel editing
  const cancelEditingItem = () => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    setEditingItem(null);
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
    }, 0);
  };

  // Also update the main component's view of shopping list
  useEffect(() => {
    console.log("Shopping list state updated:", localShoppingList);
  }, [localShoppingList]);

  // Add this useEffect to sync local state to Redux view
  useEffect(() => {
    // Only sync when localShoppingList has items and they don't match Redux
    if (localShoppingList?.items && 
        JSON.stringify(localShoppingList?.items) !== JSON.stringify(shoppingList?.items)) {
      console.log("Syncing local shopping list to Redux view");
      
      // Use the imported action
      dispatch(updateShoppingListView(localShoppingList));
    }
  }, [localShoppingList, dispatch, shoppingList?.items]);

  // Add an effect to force UI updates when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      console.log(`RefreshKey changed to ${refreshKey}, forcing UI update`);
      
      // Force redraw of the component
      const timer = setTimeout(() => {
        console.log("Refreshing UI after refreshKey change");
        
        // Ensure the local shopping list is in sync
        if (localShoppingList?.items) {
          dispatch(updateShoppingListView({
            ...localShoppingList,
            _timestamp: Date.now()
          }));
        }
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [refreshKey, dispatch, localShoppingList]);

  // Add a useEffect to update local state whenever Redux state changes significantly
  useEffect(() => {
    // Only update if Redux has shopping list items and they differ from local state
    if (shoppingList?.items && Object.keys(shoppingList.items).length > 0) {
      const reduxItemsJSON = JSON.stringify(shoppingList.items);
      const localItemsJSON = JSON.stringify(localShoppingList?.items || {});
      
      if (reduxItemsJSON !== localItemsJSON) {
        console.log("Redux shopping list changed - updating local state");
        setLocalShoppingList({
          items: shoppingList.items,
          dateRange: shoppingList.dateRange,
          listId: shoppingList.listId,
          exists: shoppingList.exists
        });
        
        // Force UI refresh
        setSelectedItems(new Set());
      }
    }
  }, [shoppingList]);

  // Add state for delete confirmation dialog
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Clear shopping list function
  const deleteShoppingList = async () => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    try {
      // Get the user ID and list ID
      const userId = user?._id || user?.id;
      const listId = shoppingList?.listId;
      
      if (!userId || !listId) {
        console.error("Missing user ID or list ID for deletion");
        return;
      }
      
      // Use the service directly
      await mealPlannerService.deleteShoppingList(userId, listId);
      
      // Also dispatch the Redux action to update the global state
      dispatch(deleteShoppingListAction(listId));
      
      // Clear local state
      setLocalShoppingList({
        items: {},
        dateRange: { start: "", end: "" },
        _timestamp: Date.now()
      });
      
      // Clear selected items
      setSelectedItems(new Set());
      
      // Force UI refresh
      setRefreshKey(prev => prev + 1);
      
      // Close the confirmation dialog
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting shopping list:", error);
    }
    
    // Restore scroll position
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto'
      });
    }, 0);
  };

  // Delete confirmation dialog component
  const DeleteConfirmationDialog = () => {
    if (!showDeleteConfirmation) return null;
    
    const listName = shoppingList?.name || "list";
    
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm bg-black/80">
        <div className="bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark p-6 rounded-xl border border-gray-700 shadow-xl max-w-md w-full mx-4">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-soft-pink/20 mr-3">
              <FaTrash className="text-soft-pink" />
            </div>
            <h2 className="text-xl font-bold text-white">Delete List?</h2>
          </div>
          
          <p className="text-gray-300 mb-6">
            Are you sure you want to delete "{listName}"? This action cannot be undone.
          </p>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDeleteConfirmation(false)}
              className="py-2.5 px-5 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-all duration-300"
            >
              Cancel
            </button>
            <button
              onClick={deleteShoppingList}
              className="py-2.5 px-5 bg-soft-pink text-white rounded-lg transition-all duration-300"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add document click handler to close dropdown when clicking outside
  useEffect(() => {
    const handleDocumentClick = () => {
      if (openShopDropdownId !== null) {
        setOpenShopDropdownId(null);
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [openShopDropdownId]);

  // Add state for the shop modal
  const [shopModalOpen, setShopModalOpen] = useState(false);
  const [selectedShopItem, setSelectedShopItem] = useState(null);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-mint-green"></div>
      </div>
    );
  }

  // Log current state for debugging
  console.log("Rendering ShoppingList with:", {
    itemCount: getTotalItems(),
    refreshKey,
    filteredGroupCount: filteredGroupedItems.length,
    timestamp: Date.now()
  });

  return (
    <div className="pb-6 max-w-5xl mx-auto">
      
      {/* Modals */}
      <GenerateShoppingListModal />
      <AddItemModal 
        show={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onAddItem={addNewItem}
      />
      {selectedShopItem && (
        <ShopModal 
          show={shopModalOpen}
          onClose={() => setShopModalOpen(false)}
          item={selectedShopItem}
          stores={groceryStores}
        />
      )}
      
      {/* Main content */}
      <>
        {/* Date range selection */}
        <div className="mb-6 bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-xl p-5 shadow-lg border border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center">
                <FaCalendarAlt className="mr-2 text-soft-pink" />
                Shopping List Date Range
              </h3>
              <div className="flex items-center gap-2 text-white">
                {dateRangeStart && dateRangeEnd ? (
                  <p className="font-medium text-white bg-gray-800/60 py-1.5 px-3 rounded-full shadow-sm border border-gray-700">
                    {format(dateRangeStart, 'MMM d, yyyy')} - {format(dateRangeEnd, 'MMM d, yyyy')}
                    <span className="ml-2 text-xs bg-gray-700 px-2 py-0.5 rounded-full text-gray-300">
                      {Math.round((dateRangeEnd.getTime() - dateRangeStart.getTime()) / (1000 * 60 * 60 * 24) + 1)} days
                    </span>
                  </p>
                ) : (
                  <p className="text-gray-400 italic">No date range selected</p>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowGenerateListModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 px-4 py-2.5 rounded-full text-pri-dark text-sm font-medium transition-all duration-300 shadow-sm hover:shadow"
                title="Change date range and generate shopping list"
              >
                <FaCalendarAlt className="text-pri-dark" />
                <span>Generate List</span>
              </button>
              
              <button 
                onClick={saveCurrentShoppingList}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-4 py-2.5 rounded-full text-gray-300 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow border border-gray-700"
                title="Save shopping list"
              >
                <FaSave className="text-mint-green" />
                <span className="hidden sm:inline">Save List</span>
              </button>
              
              <button 
                onClick={() => setShowDeleteConfirmation(true)}
                className="flex items-center gap-2 bg-gray-800 hover:bg-soft-pink/30 px-4 py-2.5 rounded-full text-gray-300 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow border border-gray-700"
                title="Delete entire shopping list"
              >
                <FaTrash className="text-soft-pink" />
                <span className="hidden sm:inline">Delete List</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Notification for unsaved shopping list */}
        <UnsavedListNotification />
        
        {/* Duplicates warning banner */}
        <DuplicatesWarningBanner />
        
        {/* Search and filters */}
        <div className="mb-6 p-5 bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-xl shadow-lg border border-gray-700">
          <div className="flex flex-col md:flex-row gap-3 mb-3">
            <div className="flex-grow relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-soft-pink" />
              <input
                type="text"
                placeholder="Search items..."
                className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-soft-pink/30 focus:border-soft-pink shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Category and action buttons - Mobile layout */}
            <div className="md:hidden">
              <div className="flex items-center justify-between gap-2">
                <select
                  className="flex-grow pl-4 pr-8 py-2.5 rounded-full border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-mint-green/30 focus:border-mint-green appearance-none shadow-sm text-sm"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.filter(Boolean).map((cat) => 
                    cat && <option key={cat} value={cat}>{cat}</option>
                  )}
                </select>
                
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => setShowAddItemModal(true)}
                    className="bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 text-pri-dark p-2.5 rounded-full flex items-center shadow-sm transition-all duration-300"
                    title="Add New Item"
                  >
                    <FaPlus className="text-xs" />
                  </button>
                  
                  <button
                    onClick={printShoppingList}
                    className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 p-2.5 rounded-full flex items-center shadow-sm transition-all duration-300"
                    title="Print List"
                  >
                    <FaPrint className="text-mint-green text-xs" />
                  </button>
                  
                  <button
                    onClick={selectAllItems}
                    className={`${selectedItems.size > 0 && selectedItems.size === getTotalItems() ? 'bg-mint-green text-pri-dark' : 'bg-gray-800 text-gray-300 border border-gray-700'} p-2.5 rounded-full flex items-center shadow-sm transition-all duration-300`}
                    title={selectedItems.size > 0 && selectedItems.size === getTotalItems() ? 'Unselect All' : 'Select All'}
                  >
                    <FaCheckSquare className="text-xs" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Desktop layout */}
            <div className="hidden md:flex gap-2 flex-wrap">
              <div className="w-full sm:w-auto">
                <select
                  className="w-full pl-4 pr-8 py-3 rounded-full border border-gray-700 bg-gray-800/60 text-white focus:outline-none focus:ring-2 focus:ring-mint-green/30 focus:border-mint-green appearance-none shadow-sm"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.filter(Boolean).map((cat) => 
                    cat && <option key={cat} value={cat}>{cat}</option>
                  )}
                </select>
              </div>
              
              <button
                onClick={() => setShowAddItemModal(true)}
                className="bg-gradient-to-r from-soft-pink to-mint-green hover:opacity-90 text-pri-dark px-4 rounded-full flex items-center shadow-sm hover:shadow transition-all duration-300"
                title="Add New Item"
              >
                <FaPlus className="mr-2" />
                <span>Add Item</span>
              </button>
              
              <button
                onClick={printShoppingList}
                className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 py-2.5 px-4 rounded-full flex items-center shadow-sm hover:shadow transition-all duration-300"
              >
                <FaPrint className="mr-2 text-mint-green" />
                <span>Print</span>
              </button>
              
              <button
                onClick={selectAllItems}
                className={`${selectedItems.size > 0 && selectedItems.size === getTotalItems() ? 'bg-mint-green text-pri-dark' : 'bg-gray-800 text-gray-300 border border-gray-700'} py-2.5 px-4 rounded-full flex items-center shadow-sm hover:shadow transition-all duration-300`}
              >
                <FaCheckSquare className="mr-2" />
                <span>{selectedItems.size > 0 && selectedItems.size === getTotalItems() ? 'Unselect All' : 'Select All'}</span>
              </button>
            </div>
          </div>
          
          {/* List info */}
          <div className="text-sm flex items-center bg-gray-800/60 p-3 rounded-lg text-gray-300">
            <FaShoppingBasket className="mr-2 text-soft-pink" />
            <span>
              <span className="font-semibold text-white">{getTotalItems()}</span> items in your shopping list
              {selectedItems.size > 0 && <span className="text-mint-green ml-1">({selectedItems.size} selected)</span>}
            </span>
          </div>
        </div>
        
        {/* Display shopping list items */}
        {filteredGroupedItems.length === 0 ? (
          <div className="text-center py-14 border-2 border-dashed border-gray-700 rounded-xl bg-gray-800/40 shadow-sm">
            {searchTerm || filterCategory ? (
              <div className="text-gray-300">
                <p className="mb-2 text-lg">No items match your filters</p>
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategory('');
                  }}
                  className="text-soft-pink hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="text-gray-300">
                <p className="mb-2 text-lg">Your shopping list is empty</p>
                <p>Create meal plans to generate a shopping list</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredGroupedItems.map((group) => (
              <div
                key={`group-${group.category}-${refreshKey}-${localShoppingList?._timestamp || Date.now()}`}
                className="bg-gradient-to-br from-sec-dark via-ter-dark to-pri-dark rounded-xl border border-gray-700 shadow-lg overflow-hidden"
              >
                <div
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-soft-pink/20 to-mint-green/20 border-b border-gray-700 cursor-pointer"
                  onClick={() => toggleCategory(group.category)}
                >
                  <div className="flex items-center">
                    <span className="font-semibold text-white">
                      {categoryNames[group.category] || group.category}
                    </span>
                    <span className="ml-2 text-xs bg-gray-700 px-2.5 py-1 rounded-full text-gray-300">
                      {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <div className="bg-gray-800/60 rounded-full w-7 h-7 flex items-center justify-center shadow-sm border border-gray-700">
                    {expandedCategories[group.category] ? (
                      <FaChevronUp className="text-gray-300" />
                    ) : (
                      <FaChevronDown className="text-gray-300" />
                    )}
                  </div>
                </div>
                
                {expandedCategories[group.category] && (
                  <div className="overflow-hidden">
                    <ul className="divide-y divide-gray-700">
                      {group.items.map((item) => (
                        <li
                          key={`${item._tempId || `${item.name}-${item.quantity}`}-${refreshKey}`}
                          className="p-4 flex items-center hover:bg-gray-800/40 transition-all duration-300"
                        >
                          <div
                            className="cursor-pointer mr-4"
                            onClick={(e) => {
                              e.preventDefault();
                              toggleItem(item._tempId || item.id);
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {selectedItems.has(item._tempId || item.id) ? (
                              <FaCheckCircle 
                                className="text-mint-green text-xl" 
                                onClick={(e) => e.preventDefault()}
                              />
                            ) : (
                              <FaCircle 
                                className="text-gray-600 text-xl hover:text-soft-pink/50 transition-colors" 
                                onClick={(e) => e.preventDefault()}
                              />
                            )}
                          </div>
                          
                          {editingItem && editingItem.id === item.id ? (
                            // Editing mode UI
                            <div className="flex-1 flex items-center gap-2">
                              <div className="flex-grow">
                                <div className="mb-1">
                                  <input
                                    type="text"
                                    value={editingItem.tempName}
                                    onChange={(e) => setEditingItem({...editingItem, tempName: e.target.value})}
                                    className="w-full px-2 py-1 text-sm rounded-md border border-gray-700 bg-gray-800 text-white focus:outline-none focus:ring-1 focus:ring-soft-pink/30"
                                    placeholder="Item name"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={editingItem.tempQuantity}
                                    onChange={(e) => setEditingItem({...editingItem, tempQuantity: e.target.value})}
                                    className="w-20 px-2 py-1 text-sm rounded-md border border-gray-700 bg-gray-800 text-white focus:outline-none focus:ring-1 focus:ring-soft-pink/30"
                                    placeholder="Qty"
                                  />
                                  <input
                                    type="text"
                                    placeholder="unit"
                                    value={editingItem.tempUnit}
                                    onChange={(e) => setEditingItem({...editingItem, tempUnit: e.target.value})}
                                    className="w-20 px-2 py-1 text-sm rounded-md border border-gray-700 bg-gray-800 text-white focus:outline-none focus:ring-1 focus:ring-soft-pink/30"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <button 
                                  onClick={saveEditedItem}
                                  className="p-1.5 bg-mint-green/20 hover:bg-mint-green/30 border border-mint-green/30 rounded-full text-mint-green"
                                  title="Save changes"
                                >
                                  <FaSave />
                                </button>
                                <button 
                                  onClick={cancelEditingItem}
                                  className="p-1.5 bg-soft-pink/20 hover:bg-soft-pink/30 border border-soft-pink/30 rounded-full text-soft-pink"
                                  title="Cancel"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Normal display UI
                            <div className="flex flex-1 justify-between items-center">
                              <span className={`${selectedItems.has(item._tempId || item.id) ? 'line-through text-gray-500' : 'text-gray-300'} font-medium`}>
                                {item.name}
                              </span>
                              <div className="flex items-center">
                                <span className="text-gray-300 text-sm bg-gray-800/60 px-3 py-1 rounded-full shadow-sm border border-gray-700 mr-2">
                                  {displayQuantity(item.quantity, item.unit)}
                                </span>
                                
                                {/* Shop button */}
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setSelectedShopItem(item);
                                    setShopModalOpen(true);
                                  }}
                                  type="button"
                                  className="p-2 bg-blue-500/20 hover:bg-blue-500/50 border border-blue-500/30 rounded-lg text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1 shadow-md mr-2"
                                  title="Shop online"
                                >
                                  <FaShoppingCart className="text-base" />
                                  <span className="text-xs font-medium hidden sm:inline">Shop</span>
                                </button>
                                
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    startEditingItem(item);
                                  }}
                                  type="button"
                                  className="p-1.5 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700 rounded-full text-gray-400 hover:text-mint-green transition-colors mr-1"
                                  title="Edit item"
                                >
                                  <FaEdit />
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    removeItem(item);
                                  }}
                                  type="button"
                                  className="p-1.5 bg-gray-800/60 hover:bg-soft-pink/30 border border-gray-700 rounded-full text-gray-400 hover:text-soft-pink transition-colors"
                                  title="Remove item"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </>
      <DeleteConfirmationDialog />
    </div>
  );
};

export default ShoppingList; 